import { useEffect, useState } from "react";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  createEntertainmentItemizer,
  updateEntertainmentItemizer,
} from "../../utils/ApiCalls/BackOffice/Entertainment";
import { addlog } from "../../utils/ApiCalls/Logs";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";

const FIELD_LABELS = {
  itemizerId: "Itemizer ID",
  itemizerCode: "Itemizer Code",
  itemizerDescription: "Description",
};

// Builds "Itemizer Code: Old -> New" from only the fields that actually changed
const buildFieldChanges = (oldValues, newValues) =>
  Object.keys(FIELD_LABELS)
    .filter((key) => String(oldValues[key]) !== String(newValues[key]))
    .map((key) => `${FIELD_LABELS[key]}: ${oldValues[key]} -> ${newValues[key]}`)
    .join(", ");

const EntertainmentItemizerInput = ({ rvcId, rvcName, itemizer, onSuccess, readOnly }) => {
  const [itemizerId, setItemizerId] = useState("");
  const [itemizerCode, setItemizerCode] = useState("");
  const [itemizerDescription, setItemizerDescription] = useState("");
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [error, setError] = useState();

  const isEdit = !!itemizer?.id;

  useEffect(() => {
    if (itemizer?.id) {
      setItemizerId(itemizer.itemizerId ?? "");
      setItemizerCode(itemizer.itemizerCode || "");
      setItemizerDescription(itemizer.itemizerDescription || "");
    }
  }, [itemizer]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (itemizerId === "" || !itemizerCode) {
      const message = "Error: The following fields are empty - itemizer id, itemizer code";
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message, type: "error" },
      });
      setError(message);
      return;
    }

    setError();

    const values = isEdit
      ? { itemizerId, itemizerCode, itemizerDescription }
      : { rvcId, itemizerId, itemizerCode, itemizerDescription };

    const data = isEdit
      ? await updateEntertainmentItemizer(itemizer.id, values)
      : await createEntertainmentItemizer(values);

    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      return;
    }

    dispatch({
      type: "SET_ERROR_MESSAGE",
      errMess: {
        message: isEdit ? "Itemizer updated" : "Itemizer created",
        type: "success",
      },
    });

    let action = LOG_ACTIONS.CREATED;
    if (isEdit) {
      const changes = buildFieldChanges(
        {
          itemizerId: itemizer.itemizerId,
          itemizerCode: itemizer.itemizerCode,
          itemizerDescription: itemizer.itemizerDescription,
        },
        values
      );
      action = changes ? `${LOG_ACTIONS.UPDATED} (${changes})` : LOG_ACTIONS.UPDATED;
    }

    addlog(
      action,
      LOG_TYPES.ENTERTAINMENT_RVC,
      {
        id: itemizer?.id || data?.data?.id,
        rvcId,
        title: `${rvcName ? rvcName + " - " : ""}${itemizerCode}`,
        category: ENTERTAINMENT_LOG_CATEGORIES.ITEMIZER,
      },
      userDetails.id
    );

    onSuccess?.(data?.data || data);
  };

  return (
    <div>
      <section className="content">
        {error && (
          <div className="d-flex justify-content-center align-items-center text-danger">
            <p>{error}</p>
          </div>
        )}

        <div className="container-fluid">
          <div className="card" style={readOnly ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
            <form className="input_form" onSubmit={submitHandler}>
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="itemizerId" className="f_label">Itemizer ID <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="itemizerId"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={itemizerId}
                        onChange={(e) => setItemizerId(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="itemizerCode" className="f_label">Itemizer Code <span style={{ color: "red" }}>*</span></label>
                      <input
                        id="itemizerCode"
                        required
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={itemizerCode}
                        onChange={(e) => setItemizerCode(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="fieldrow">
                      <label htmlFor="itemizerDescription" className="f_label">Description</label>
                      <input
                        id="itemizerDescription"
                        disabled={readOnly}
                        className="f_input"
                        type="text"
                        value={itemizerDescription}
                        onChange={(e) => setItemizerDescription(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!readOnly && (
                <div className="card-footer text-right">
                  <button className="btn main_submit">
                    {isEdit ? "Update" : "Add"} itemizer
                  </button>
                </div>
              )}
            </form>
          </div>
          <div className="pt-3"></div>
        </div>
      </section>
    </div>
  );
};

export default EntertainmentItemizerInput;
